import React, { useMemo, useState } from 'react';
import AnalyticsSettingsPanel, { useAnalyticsSettings, SettingsButton } from '../common/AnalyticsSettings';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const AVAILABLE_METRICS = [
  { id: 'totalRecords', label: 'Total Records', icon: '📋', defaultVisible: true },
  { id: 'totalRentalCost', label: 'Total Rental Cost', icon: '💵', defaultVisible: true },
  { id: 'totalFuelCost', label: 'Total Fuel Cost', icon: '⛽', defaultVisible: true },
  { id: 'avgDuration', label: 'Avg Duration', icon: '⏱️', defaultVisible: true },
];

const AVAILABLE_CHARTS = [
  { id: 'byDescription', label: 'By Description', icon: '📦', defaultVisible: true, defaultType: 'bar' },
  { id: 'byPeriodType', label: 'By Period Type', icon: '📅', defaultVisible: true, defaultType: 'bar' },
  { id: 'costBreakdown', label: 'Cost Breakdown', icon: '🥧', defaultVisible: true, defaultType: 'donut' },
];

/**
 * RentalsAnalyticsDashboard - Analytics Dashboard for Rentals Log
 * Displays rental costs, equipment usage, and duration analysis
 */
const RentalsAnalyticsDashboard = ({ data, onClose }) => {
  const [selectedView, setSelectedView] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState } = usePresence('rentalsAnalytics', 'global', true);
  
  const {
    settings,
    updateSetting,
    toggleMetric,
    toggleChart,
    setChartType,
    resetToDefaults,
    isMetricVisible,
    isChartVisible,
  } = useAnalyticsSettings('rentals_log', AVAILABLE_METRICS, AVAILABLE_CHARTS);

  const analytics = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalRecords: 0, totalRentalCost: 0, totalFuelCost: 0, totalSurcharge: 0, totalMobDem: 0,
        avgDuration: 0, uniqueDescriptions: 0, byDescription: [], byPeriodType: [], costBreakdown: [],
      };
    }

    const totalRecords = data.length;
    const totalRentalCost = data.reduce((sum, d) => sum + (parseFloat(d.basic_rental) || 0), 0);
    const totalFuelCost = data.reduce((sum, d) => sum + (parseFloat(d.fuel_cost) || 0), 0);
    const totalSurcharge = data.reduce((sum, d) => sum + (parseFloat(d.surcharge) || 0), 0);
    const totalMobDem = data.reduce((sum, d) => sum + (parseFloat(d.mob_dem) || 0), 0);
    const avgDuration = totalRecords > 0 ? data.reduce((sum, d) => sum + (parseFloat(d.duration) || 0), 0) / totalRecords : 0;
    const uniqueDescriptions = new Set(data.map(d => d.description).filter(Boolean)).size;

    // By Description
    const descMap = {};
    data.forEach(d => {
      const desc = d.description || 'Unspecified';
      if (!descMap[desc]) descMap[desc] = { name: desc, count: 0, totalCost: 0 };
      descMap[desc].count++;
      descMap[desc].totalCost += (parseFloat(d.basic_rental) || 0) + (parseFloat(d.fuel_cost) || 0) + (parseFloat(d.surcharge) || 0);
    });
    const byDescription = Object.values(descMap).sort((a, b) => b.totalCost - a.totalCost).slice(0, 15);

    // By Period Type
    const periodMap = {};
    data.forEach(d => {
      const period = d.period_type || 'Unspecified';
      if (!periodMap[period]) periodMap[period] = { name: period, count: 0, totalCost: 0 };
      periodMap[period].count++;
      periodMap[period].totalCost += (parseFloat(d.basic_rental) || 0);
    });
    const byPeriodType = Object.values(periodMap).sort((a, b) => b.count - a.count);

    const costBreakdown = [
      { name: 'Basic Rental', value: totalRentalCost },
      { name: 'Fuel Cost', value: totalFuelCost },
      { name: 'Surcharge', value: totalSurcharge },
      { name: 'Mob/Dem', value: totalMobDem },
    ];

    return { totalRecords, totalRentalCost, totalFuelCost, totalSurcharge, totalMobDem, avgDuration, uniqueDescriptions, byDescription, byPeriodType, costBreakdown, grandTotal: totalRentalCost + totalFuelCost + totalSurcharge + totalMobDem };
  }, [data]);

  const colors = { primary: '#6366f1', secondary: '#8b5cf6', success: '#10b981', warning: '#f59e0b', danger: '#ef4444', info: '#3b82f6', dark: '#1e293b', chartColors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'] };

  const formatCurrency = (num) => {
    if (num >= 1000000) return '$' + (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'K';
    return '$' + num.toFixed(2);
  };

  const MetricCard = ({ title, value, subtitle, icon, color }) => (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.dark, margin: '0.5rem 0' }}>{value}</div>
      {subtitle && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</span>}
    </div>
  );

  const BarChart = ({ data, valueKey, labelKey, title, formatValue }) => {
    const chartData = data.slice(0, 10);
    const maxValue = Math.max(...chartData.map(d => d[valueKey] || 0), 1);
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {chartData.map((item, idx) => {
            const value = item[valueKey] || 0;
            const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '120px', fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item[labelKey]}</div>
                <div style={{ flex: 1, height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', backgroundColor: colors.chartColors[idx % colors.chartColors.length], borderRadius: '4px' }} />
                </div>
                <div style={{ width: '70px', fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>{formatValue ? formatValue(value) : value.toFixed(0)}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)', zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
      <div style={{ backgroundColor: '#f1f5f9', borderRadius: '16px', width: '100%', maxWidth: '1400px', maxHeight: '95vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
        <div style={{ padding: '1.25rem 1.5rem', background: `linear-gradient(135deg, ${colors.warning} 0%, ${colors.success} 100%)`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>🚜 Rentals Analytics</h2>
            <p style={{ margin: '0.25rem 0 0', opacity: 0.9, fontSize: '0.85rem' }}>Rental costs, equipment usage, and duration analysis</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            <button onClick={onClose} style={{ padding: '0.5rem 1rem', backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem' }}>✕ Close</button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {analytics.totalRecords === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
              <h3 style={{ margin: 0, color: colors.dark }}>No Data Available</h3>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <MetricCard title="Total Items" value={analytics.totalRecords} subtitle={`${analytics.uniqueDescriptions} unique`} icon="📦" color={colors.primary} />
                <MetricCard title="Basic Rental" value={formatCurrency(analytics.totalRentalCost)} icon="💵" color={colors.success} />
                <MetricCard title="Fuel Cost" value={formatCurrency(analytics.totalFuelCost)} icon="⛽" color={colors.warning} />
                <MetricCard title="Surcharge" value={formatCurrency(analytics.totalSurcharge)} icon="📈" color={colors.danger} />
                <MetricCard title="Grand Total" value={formatCurrency(analytics.grandTotal)} icon="💰" color={colors.info} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
                <BarChart data={analytics.byDescription} valueKey="totalCost" labelKey="name" title="Cost by Equipment" formatValue={formatCurrency} />
                <BarChart data={analytics.byPeriodType} valueKey="count" labelKey="name" title="Items by Period Type" />
              </div>
            </>
          )}
        </div>
        <div style={{ padding: '0.75rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0', fontSize: '0.75rem', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
          <span>{analytics.totalRecords} items</span>
          <span>Total: {formatCurrency(analytics.grandTotal)}</span>
        </div>
      </div>
    </div>
  );
};

export default RentalsAnalyticsDashboard;

